import React, { useState, useEffect, useRef } from 'react'
import InputTextSF from '../../../widgets/form/InputTextSF'

const ALMReportTableUI = ({
    tableKey,
    title,
    trackerMap,
    onAddMetricClick,
    onPendingUpdatesChange,
    onSavePendingUpdates,
    onShowDelinquentClick
}) => {
    const [editingCell, setEditingCell] = useState({})
    const [editingValue, setEditingValue] = useState('')
    const [pendingUpdates, setPendingUpdates] = useState([])
    const months = trackerMap ? Object.keys(trackerMap).sort() : []
    const firstMonth = months[0]
    const openingBalanceField =
        tableKey === 'goodStandingAccounts'
            ? 'openingBalanceGoodStanding'
            : 'openingBalance'
    const OUTFLOW_ORDER = [
        "Disbursement",
        "DFC Principal",
        "DFC Interest",
        "SBI EMI",
        "OD Interest",
        "Salary",
        "Other Expenses"
    ];
    const getFinancialYear = (yearMonth) => {
        const [year, month] = yearMonth.split('-').map(Number)

        return month >= 4
            ? `FY ${year}-${String(year + 1).slice(2)}`
            : `FY ${year - 1}-${String(year).slice(2)}`
    }
    const getCellKey = (month, metric) =>
        `${month}_${metric.key}`
    const formatMonth = (yearMonth) => {
        const [year, month] = yearMonth.split('-')

        const monthName = new Date(year, month - 1)
            .toLocaleString('default', { month: 'short' })

        return `${monthName}-${year.substring(2)}`
    }

    const formatFinancialYear = (fy) => {
        const [startYear, endYear] = fy.replace('FY ', '').split('-')

        return `FY ${startYear}-${endYear}`
    }

    const fyMap = {}

    months.forEach(month => {
        const fy = getFinancialYear(month)

        if (!fyMap[fy]) {
            fyMap[fy] = []
        }

        fyMap[fy].push(month)
    })
    const updatePendingList = (currentList, update, updateState = true) => {

        const list = [...currentList]

        const index = list.findIndex(item =>
            item.bucketMonth === update.bucketMonth &&
            item.metricType === update.metricType &&
            item.metricKey === update.metricKey
        )

        if (index >= 0) {
            list[index] = update
        } else {
            list.push(update)
        }

        if (updateState) {
            setPendingUpdates(list);

            if (onPendingUpdatesChange) {
                onPendingUpdatesChange(list);
            }
        }
        return list
    }
    const saveCurrentEditingCell = () => {

        if (!editingCell || !editingCell.metric) {
            return null
        }

        if (String(editingCell.value).trim() === String(editingCell.originalValue).trim()) {
            return null
        }
        let update

        if (editingCell.metric.type === 'OPENING BALANCE') {
            const startMonth = editingCell.month || firstMonth;
            update = {
                bucketMonth: startMonth,
                metricType: 'OPENING BALANCE',
                metricKey: editingCell.metric.key || openingBalanceField,
                value: String(editingCell.value)
            };
        } else {
            update = {
                bucketMonth: editingCell.month,
                metricType:
                    editingCell.metric.mapType === 'inflowMetrics'
                        ? 'INFLOW'
                        : 'OUTFLOW',
                metricKey: editingCell.metric.key,
                value: String(editingCell.value)
            }
        }

        let request = updatePendingList(pendingUpdates, update, false);
        request = updateFutureOpeningBalances(
            editingCell.month || firstMonth,
            request
        );

        setPendingUpdates(request);

        if (onPendingUpdatesChange) {
            onPendingUpdatesChange(request);
        }

        return request
    }
    const moveToCell = (currentColumn, currentMetric, rowOffset, columnOffset) => {

        const editableColumns = columns.filter(c => c.months.length === 1);

        const columnIndex = editableColumns.findIndex(
            c => c.key === currentColumn.key
        );

        const rowIndex = editableMetrics.findIndex(
            m => m.key === currentMetric.key
        );

        const newColumnIndex = columnIndex + columnOffset;
        const newRowIndex = rowIndex + rowOffset;

        if (
            newColumnIndex < 0 ||
            newColumnIndex >= editableColumns.length ||
            newRowIndex < 0 ||
            newRowIndex >= editableMetrics.length
        ) {
            return;
        }

        const newColumn = editableColumns[newColumnIndex];
        const newMetric = editableMetrics[newRowIndex];

        const value = getMetricValue(
            getBucket(newColumn.months[0]),
            newMetric
        );

        setEditingCell({
            cellKey: getCellKey(newColumn.months[0], newMetric),
            month: newColumn.months[0],
            metric: newMetric,
            value: value || '',
            originalValue: value || ''
        });
    };
    const financialYears = Object.keys(fyMap).sort()
    const currentFinancialYear = getFinancialYear(new Date().toISOString().slice(0, 7))
    const defaultExpandedFY = financialYears.includes(currentFinancialYear)
        ? currentFinancialYear
        : financialYears[0]
    const [expandedFYs, setExpandedFYs] = useState(
        defaultExpandedFY ? [defaultExpandedFY] : []
    )

    const toggleFYExpansion = (fy) => {
        setExpandedFYs(prev =>
            prev.includes(fy)
                ? prev.filter(item => item !== fy)
                : [...prev, fy]
        )
    }

    const isEditableMetric = metric =>
        ![
            'totalPrincipal',
            'totalInterest',
            'dsraReleaseAmount'
        ].includes(metric.key)
    if (!trackerMap || months.length === 0) {
        return null
    }


    const columns = financialYears.reduce((list, fy) => {
        if (expandedFYs.includes(fy)) {
            fyMap[fy].forEach(month => {
                list.push({
                    key: month,
                    label: formatMonth(month),
                    months: [month],
                    fy
                })
            })
        } else {
            list.push({
                key: fy,
                label: 'Total',
                months: fyMap[fy],
                fy
            })
        }

        return list
    }, [])

    const baseInflowMetrics = [
        { key: 'totalPrincipal', label: 'Principal' },
        { key: 'totalInterest', label: 'Interest' }
    ]

    const baseOutflowMetrics = [
        { key: 'dsraReleaseAmount', label: 'DSRA Release' }
    ]

    const getBucket = (month) => {
        return trackerMap[month] || {}
    }

    const parseAmount = (value) => {
        if (value === null || value === undefined || value === '') {
            return 0
        }

        if (typeof value === 'number') {
            return value
        }

        const normalized = String(value).replace(/,/g, '').replace(/[()]/g, '').trim()
        const amount = Number(normalized)

        if (Number.isNaN(amount)) {
            return 0
        }

        return String(value).includes('(') ? -amount : amount
    }

    const formatAmount = (value) => {
        if (value === null || value === undefined || value === '' || value === 0 || value === "0.00" || value === "0") {
            return '-'
        }

        const number = Number(value)

        if (Number.isNaN(number)) {
            return value
        }

        return number.toLocaleString('en-IN', {
            maximumFractionDigits: 2
        })
    }
    const isValidAmount = (value) => {
        if (value == null) {
            return false;
        }

        const normalized = String(value)
            .replace(/,/g, '')
            .replace(/[()]/g, '')
            .trim();

        if (normalized === '') {
            return false;
        }

        return !Number.isNaN(Number(normalized));
    };

    const round2 = value =>
        Math.round((Number(value) + Number.EPSILON) * 100) / 100;

    const updatePendingUpdatesOnly = () => {
        return saveCurrentEditingCell();
    }
    const updateFutureOpeningBalances = (
        changedMonth,
        currentPendingUpdates
    ) => {

        const startIndex = months.indexOf(changedMonth);

        if (startIndex === -1) {
            return currentPendingUpdates;
        }

        let list = [...currentPendingUpdates];

        for (let i = startIndex + 1; i < months.length; i++) {

            const previousMonth = months[i - 1];
            const currentMonth = months[i];

            const openingBalance = round2(
                getClosingBalance(previousMonth, list)
            );

            list = updatePendingList(
                list,
                {
                    bucketMonth: currentMonth,
                    metricType: 'OPENING BALANCE',
                    metricKey: openingBalanceField,
                    value: String(openingBalance)
                },
                false
            );
        }

        return list;
    };

    const getOpeningBalance = (
        month,
        currentPendingUpdates = pendingUpdates
    ) => {
        const index = months.indexOf(month);

        const pending = currentPendingUpdates.find(item =>
            item.bucketMonth === month &&
            item.metricType === 'OPENING BALANCE'
        );

        if (pending) {
            return parseAmount(pending.value);
        }

        if (
            month === firstMonth &&
            editingCell &&
            editingCell.metric &&
            editingCell.metric.type === 'OPENING BALANCE'
        ) {
            return parseAmount(editingCell.value);
        }

        if (index === 0) {
            const backendValue = getBucket(month)[openingBalanceField];

            if (
                backendValue !== null &&
                backendValue !== undefined &&
                backendValue !== ''
            ) {
                return parseAmount(backendValue);
            }

            return 0;
        }

        return getClosingBalance(
            months[index - 1],
            currentPendingUpdates
        );
    }

    const getClosingBalance = (
        month,
        currentPendingUpdates = pendingUpdates
    ) =>
        round2(
            getOpeningBalance(month, currentPendingUpdates) +
            getNetCashMovement(month, currentPendingUpdates)
        );

    const getMetricValue = (
        record,
        metric,
        month = null,
        currentPendingUpdates = pendingUpdates
    ) => {
        if (
            month &&
            editingCell &&
            editingCell.month === month &&
            editingCell.metric &&
            editingCell.metric.key === metric.key
        ) {
            return editingCell.value;
        }

        if (metric.mapType) {
            return record[metric.mapType]
                ? record[metric.mapType][metric.key]
                : null;
        }

        return record[metric.key];
    }

    const hasMetricValue = (metric) => {
        return months.some(month => {
            const value = getMetricValue(getBucket(month), metric)

            return value !== null && value !== undefined
        })
    }

    const getMapMetricKeys = (mapType) => {
        const metricKeys = new Set();

        months.forEach(month => {
            const metricMap = getBucket(month)[mapType] || {};

            Object.keys(metricMap).forEach(key => metricKeys.add(key));
        });

        const keys = [...metricKeys];

        if (mapType === "outflowMetrics") {
            return keys.sort((a, b) => {
                const ia = OUTFLOW_ORDER.indexOf(a);
                const ib = OUTFLOW_ORDER.indexOf(b);

                if (ia === -1 && ib === -1) return a.localeCompare(b);
                if (ia === -1) return 1;
                if (ib === -1) return -1;

                return ia - ib;
            });
        }

        return keys;
    };

    const formatMetricLabel = (key) => {
        return key
            .replace(/_/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\b\w/g, char => char.toUpperCase())
    }

    const inflowMetrics = [
        ...baseInflowMetrics.filter(hasMetricValue),
        ...getMapMetricKeys('inflowMetrics').map(key => ({
            key,
            label: formatMetricLabel(key),
            mapType: 'inflowMetrics',
            editable: true
        }))
    ]

    const outflowMetrics = [
        ...baseOutflowMetrics.filter(hasMetricValue),
        ...getMapMetricKeys('outflowMetrics').map(key => ({
            key,
            label: formatMetricLabel(key),
            mapType: 'outflowMetrics',
            editable: true
        }))
    ]

    const editableMetrics = [
        ...inflowMetrics.filter(isEditableMetric),
        ...outflowMetrics.filter(isEditableMetric)
    ];


    const getColumnAmount = (column, metric) => {
        if (column.months.length === 1) {
            return getMetricValue(
                getBucket(column.months[0]),
                metric,
                column.months[0]
            );
        }

        return column.months.reduce((total, month) => {
            return total + parseAmount(
                getMetricValue(getBucket(month), metric, month)
            );
        }, 0);
    }

    const getTotalAmount = (column, metrics) => {
        return metrics.reduce((total, metric) => {
            return total + parseAmount(getColumnAmount(column, metric))
        }, 0)
    }

    // const getLoanCountTitle = (column) => {
    //     const loanCount = column.months.reduce((total, month) => {
    //         return total + parseAmount(getBucket(month).loanCount)
    //     }, 0)

    //     return `Loan Count: ${loanCount}`
    // }


    const renderAmount = (column, metric) => {
        const value = getColumnAmount(column, metric)
        const editable =
            column.months.length === 1 &&
            isEditableMetric(metric)
        if (column.months.length === 1) {
            return value || '-'
        }

        return formatAmount(value)
    }

    const renderAmountCell = (column, metric) => {

        if (column.months.length !== 1 || !isEditableMetric(metric)) {
            return renderAmount(column, metric)
        }

        const month = column.months[0]
        const cellKey = getCellKey(month, metric)
        const metricValue = getMetricValue(getBucket(month), metric)
        const pending = pendingUpdates.find(item =>
            item.bucketMonth === month &&
            item.metricType === (
                metric.mapType === 'inflowMetrics'
                    ? 'INFLOW'
                    : 'OUTFLOW'
            ) &&
            item.metricKey === metric.key
        )
        const backendValue =
            metricValue != null
                ? String(metricValue)
                : ''
        const value = pending
            ? String(pending.value)
            : backendValue
        if (!editingCell || editingCell.cellKey !== cellKey) {

            return (
                <div
                    className={`alm_amount_input ${!isValidAmount(value) ? 'invalid' : ''
                        }`}
                    onClick={() => {
                        if (editingCell && editingCell.cellKey !== cellKey) {
                            saveCurrentEditingCell()
                        }

                        setEditingCell({
                            cellKey,
                            month,
                            metric,
                            value,
                            originalValue: backendValue
                        })
                    }}
                >
                    {formatAmount(value)}
                </div >
            )
        }

        return (
            <input
                autoFocus
                type='text'
                value={editingCell.value}
                className={`alm_amount_input ${!isValidAmount(editingCell.value) ? 'invalid' : ''
                    }`}
                onChange={(event) => {
                    const value = event.target.value

                    setEditingCell(prev => ({
                        ...prev,
                        value
                    }))
                }}
                onKeyDown={(event) => {

                    if (event.key === 'Enter') {

                        const request = saveCurrentEditingCell();

                        const finalRequest = request || pendingUpdates;

                        if (
                            finalRequest &&
                            finalRequest.length > 0 &&
                            onSavePendingUpdates
                        ) {
                            onSavePendingUpdates(finalRequest);
                        }

                    }
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        saveCurrentEditingCell();
                        moveToCell(column, metric, 1, 0);
                        return;
                    }

                    if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        saveCurrentEditingCell();
                        moveToCell(column, metric, -1, 0);
                        return;
                    }
                    if (event.key === 'Tab') {
                        event.preventDefault()
                        saveCurrentEditingCell()
                        if (event.shiftKey) {
                            moveToCell(column, metric, 0, -1)
                        } else {
                            moveToCell(column, metric, 0, 1)
                        }

                        return
                    }


                    if (event.key === 'Escape') {
                        setEditingCell(null)
                    }

                }}
                onFocus={(e) => e.target.select()}
            />
        )
    }
    const renderOpeningCashInput = () => {

        const month = firstMonth;
        const cellKey = 'totalOpeningCash';

        const backendValue = String(getOpeningBalance(month));

        const pending = pendingUpdates.find(item =>
            item.bucketMonth === month &&
            item.metricType === 'OPENING BALANCE'
        );

        const value = pending
            ? String(pending.value)
            : backendValue

        const isEditing =
            editingCell != null &&
            editingCell.cellKey === cellKey;

        return (
            <input
                type='text'
                className={`alm_amount_input ${isEditing && !isValidAmount(editingCell.value)
                    ? 'invalid'
                    : ''
                    }`}
                value={isEditing ? editingCell.value : value}
                onChange={event => {
                    const newValue = event.target.value;

                    setEditingCell(prev => ({
                        ...prev,
                        value: newValue
                    }));
                }}
                onKeyDown={event => {

                    if (event.key === 'Enter') {

                        const request = saveCurrentEditingCell();

                        const finalRequest = request || pendingUpdates;

                        if (
                            finalRequest &&
                            finalRequest.length > 0 &&
                            onSavePendingUpdates
                        ) {
                            onSavePendingUpdates(finalRequest);
                        }

                        setEditingCell({});
                    }

                    if (event.key === 'Tab') {
                        event.preventDefault();
                        const request = saveCurrentEditingCell();
                        const finalRequest = request || pendingUpdates;

                        if (
                            finalRequest &&
                            finalRequest.length > 0 &&
                            onSavePendingUpdates
                        ) {
                            onSavePendingUpdates(finalRequest);
                        }
                    }
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();

                        const firstColumn = columns.find(c => c.months.length === 1);
                        const firstMetric = editableMetrics[0];

                        if (firstColumn && firstMetric) {
                            setEditingCell({
                                cellKey: getCellKey(firstColumn.months[0], firstMetric),
                                month: firstColumn.months[0],
                                metric: firstMetric,
                                value: getMetricValue(
                                    getBucket(firstColumn.months[0]),
                                    firstMetric
                                ) || '',
                                originalValue: getMetricValue(
                                    getBucket(firstColumn.months[0]),
                                    firstMetric
                                ) || ''
                            });
                        }

                        return;
                    }

                    if (event.key === 'Escape') {
                        setEditingCell(null);
                    }
                }}
                onFocus={() => {
                    if (!isEditing) {
                        setEditingCell({
                            cellKey,
                            month,
                            metric: {
                                key: openingBalanceField,
                                type: 'OPENING BALANCE'
                            },
                            value,
                            originalValue: backendValue
                        });
                    }
                }}
            />
        );
    };
    const renderMetricRow = (section, metric, rowSpan) =>
        <tr key={`${section}-${metric.key}`}>
            {rowSpan &&
                <td className='section g_sticky_left section_label' rowSpan={rowSpan}>
                    {section}
                </td>
            }

            <td className='metric g_sticky_left'>
                {metric.label}
            </td>

            {columns.map(column => {
                const readOnly =
                    column.months.length !== 1 ||
                    !isEditableMetric(metric)

                return (
                    <td
                        key={column.key}
                        className={`amount ${readOnly ? 'alm_readonly_cell' : ''}`}
                    >
                        {renderAmountCell(column, metric)}
                    </td>
                )
            })}
        </tr>

    const renderAddMetricRow = (section, metricType, sectionRowSpan) =>
        <tr className='add_metric_row '>
            {
                sectionRowSpan &&
                <td className='section g_sticky_left section_label' rowSpan={sectionRowSpan}>{section}</td>
            }
            <td className='metric g_sticky_left'>
                <span
                    className='g_link_2'
                    onClick={onAddMetricClick ? () => onAddMetricClick({
                        tableKey,
                        title,
                        metricType,
                        firstMonth: months[0]
                    }) : undefined}
                >
                    <span className='icon icon-plus' /> Add {section} Metric
                </span>
            </td>
            {
                columns.map(column =>
                    <td key={column.key}></td>
                )
            }
        </tr>

    const renderTotalRow = (section, label, metrics) =>
        <tr className='total_row'>
            <td className='metric g_sticky_left'><b>{label}</b></td>
            {
                columns.map(column =>
                    <td key={column.key} className='amount'>
                        <b>{formatAmount(getTotalAmount(column, metrics))}</b>
                    </td>
                )
            }
        </tr>
    const getNetCashMovement = (
        month,
        currentPendingUpdates = pendingUpdates
    ) => {
        const bucket = getBucket(month)

        const inflow = inflowMetrics.reduce((total, metric) => {
            return total + parseAmount(
                getMetricValue(bucket, metric, month, currentPendingUpdates)
            );
        }, 0);

        const outflow = outflowMetrics.reduce((total, metric) => {
            return total + parseAmount(
                getMetricValue(bucket, metric, month, currentPendingUpdates)
            );
        }, 0);

        return round2(inflow - outflow);
    }
    const renderBalanceRow = (label, getter, editable = false) =>
        <tr className='balance_row'>
            <td className='section g_sticky_left section_label'></td>

            <td className='metric g_sticky_left'>
                <b>{label}</b>
            </td>

            {
                columns.map(column => {
                    const balance = getter(column.months[0]);
                    if (column.months.length === 1) {
                        return (
                            <td
                                key={column.key}
                                className={`amount ${!editable || column.months[0] !== firstMonth
                                    ? 'alm_readonly_cell'
                                    : ''
                                    }`}
                            >
                                {
                                    editable
                                        ? renderOpeningCashInput()
                                        : <b>{formatAmount(getter(column.months[0]))}</b>
                                }
                            </td>
                        )
                    }

                    const month = editable
                        ? column.months[0]
                        : column.months[column.months.length - 1]

                    return (
                        <td
                            key={column.key}
                            className='amount'
                        >
                            <b>{formatAmount(getter(month))}</b>
                        </td>
                    )
                })
            }
        </tr>
    return (
        <>
            <h3 className='g_text_center'>{title}</h3>
            {tableKey === 'goodStandingAccounts' && onShowDelinquentClick && (
                <div className='g_text_right g_m_bottom_10' style={{ paddingRight: '16px' }}>
                    <span
                        className='g_link'
                        style={{ fontWeight: 'bold' }}
                        onClick={onShowDelinquentClick}
                    >
                        Delinquent Account List
                    </span>
                </div>
            )}
            <div className="alm_opening_cash_container">
                <span><b> Total Opening Cash </b></span>{renderOpeningCashInput()}
            </div>
            <div className="alm_note">
                <b>Note:</b> Click a cell and press <b>Enter</b> to save the updated value.
            </div>
            {/* {
                saveError &&
                <div className='alm_save_error'>
                    <span className='icon icon-warning' />
                    Unable to save changes. Please try again.
                </div>
            } */}
            <div className='alm_table_scroll'>
                <table className='alm_table'>
                    <thead>
                        <tr>
                            <th className='section g_sticky_left'></th>
                            <th className='metric g_sticky_left'></th>
                            {
                                financialYears.map(fy => {
                                    const isExpanded = expandedFYs.includes(fy)
                                    return (
                                        <th
                                            key={fy}
                                            colSpan={isExpanded ? fyMap[fy].length : 1}
                                            className={`fy ${isExpanded ? 'expanded' : ''}`}
                                            onClick={() => toggleFYExpansion(fy)}
                                        >
                                            <span className={`icon ${isExpanded ? 'icon-angle-down' : 'icon-angle-right'}`} />
                                            {formatFinancialYear(fy)}
                                        </th>
                                    )
                                })
                            }
                        </tr>

                        <tr>
                            <th className='section g_sticky_left'>Section</th>
                            <th className='metric g_sticky_left'>Metric</th>
                            {
                                columns.map(column =>
                                    <th key={column.key} className='month g_no_wrap_2'>{column.label}</th>
                                )
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {/* {renderBalanceRow('Opening Balance', getOpeningBalance, true)} */}
                        {
                            inflowMetrics.length > 0 &&
                            inflowMetrics.map((metric, index) =>
                                renderMetricRow('Inflow', metric, index === 0 ? inflowMetrics.length + 2 : null)
                            )
                        }
                        {renderAddMetricRow('Inflow', 'inflow', inflowMetrics.length === 0 ? 2 : null)}
                        {renderTotalRow('Inflow', 'Total Inflow', inflowMetrics)}
                        <tr className="alm_section_gap">
                            <td colSpan={columns.length + 2}></td>
                        </tr>
                        {
                            outflowMetrics.length > 0 &&
                            outflowMetrics.map((metric, index) =>
                                renderMetricRow('Outflow', metric, index === 0 ? outflowMetrics.length + 2 : null)
                            )
                        }
                        {renderAddMetricRow('Outflow', 'outflow', outflowMetrics.length === 0 ? 2 : null)}
                        {renderTotalRow('Outflow', 'Total Outflow', outflowMetrics)}

                        <tr className="alm_section_gap">
                            <td colSpan={columns.length + 2}></td>
                        </tr>
                        <tr className='net_cash_row'>
                            <td className='section g_sticky_left section_label'>Net Cash Movement</td>
                            <td className='metric g_sticky_left'><b>Net Cash Movement</b></td>
                            {
                                columns.map(column => {
                                    const netCashMovement =
                                        column.months.length === 1
                                            ? getNetCashMovement(column.months[0])
                                            : column.months.reduce(
                                                (total, month) => total + getNetCashMovement(month),
                                                0
                                            )
                                    return (
                                        <td
                                            key={column.key}
                                            className={`amount ${netCashMovement < 0 ? 'g_error' : ''}`}
                                        >
                                            <b>{formatAmount(netCashMovement)}</b>
                                        </td>
                                    )
                                })
                            }
                        </tr>
                        <tr className="alm_section_gap">
                            <td colSpan={columns.length + 2}></td>
                        </tr>
                        {renderBalanceRow('Closing Balance', getClosingBalance)}
                    </tbody>

                </table>
            </div>
            {/* <pre>
                {JSON.stringify(pendingUpdates, null, 2)}
            </pre> */}
        </>
    )
}

export default ALMReportTableUI
